import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { from, Observable, Subject, switchMap } from 'rxjs';
import { KnowledgeClient } from '../../../core/models/knowledge-client-dto';
import { KnowledgeFileDto } from '../../../core/models/knowledge-file-dto';
import { KnowledgeChatResponse } from '../../../core/models/knowledge-chat-response';
import { KnowledgeChatRequest } from '../../../core/models/knowledge-chat-request';
import { ResponseProcessInfoDto } from '../../../core/models/knoledge-file-deletion-response';
import { KnowledgeProjectDTO } from '../../../core/models/knowledge-project-dto';
import { AuthService } from '../../../core/auth/services/auth.service';
import { PageResult } from '../../../core/models/page-result';
import { SharePointSiteInfo } from '../../../core/models/sharepoint-site-info';
import { SharePointDriveInfo } from '../../../core/models/sharepoint-drive-info';
import { SharePointFileInfo } from '../../../core/models/sharepoint-file-info';
import { SharePointImportRequest, SharePointBatchImportRequest } from '../../../core/models/sharepoint-import-request';
import { SharePointImportResponse, SharePointBatchImportResponse } from '../../../core/models/sharepoint-import-response';
import { SharePointSyncResponse } from '../../../core/models/sharepoint-sync-response';
import { SuggestionDto } from '../../../core/models/suggestion-dto';
import { KnowledgeFolderDto } from '../../../core/models/knowledge-folder-dto';
import { MetadataInfo } from '../../../core/models/knowledge-chat-response';
import { ChatHistoryDetail, ChatHistoryEntry } from '../../../core/models/chat-history-entry';

export interface StreamEvent {
  type: 'metadata' | 'answer' | 'done' | 'error' | 'tool_call' | 'tool_result';
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class KnowledgeService {

  private knowledgeResource = 'knowledge';
  private readonly apiKnowledge: string;

  private readonly downloadRoles = [
    'ADMIN',
    'MANAGER'
  ];

  hasDownloadPermission(userRoles: string[]): boolean {
    return userRoles.some(role =>
      this.downloadRoles.some(allowed => role.includes(allowed))
    );
  }

  constructor(private http: HttpClient, private authService: AuthService, private ngZone: NgZone) {
    this.apiKnowledge = `${environment.knowledgeApiUrl}`;
  }

  getClientsAvailable(): Observable<KnowledgeClient[]> {
    return this.http.get<KnowledgeClient[]>(`${this.apiKnowledge}/${this.knowledgeResource}/clients`);
  }

  uploadFile(projectId: string, file: File): Observable<any> {
    const headers = {
      'content-type': 'multipart/form-data'
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    return this.http.post<any>(`${this.apiKnowledge}/${this.knowledgeResource}/files/upload-file`, formData);
  }

  uploadMultipleFiles(projectId: string, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('projectId', projectId);
    return this.http.post<any>(`${this.apiKnowledge}/${this.knowledgeResource}/files/upload-files`, formData);
  }

  getProjectFiles(
    projectUUID: string,
    page: number = 0,
    size: number = 10,
    filter: string = '',
    folderId?: string | null
  ): Observable<PageResult<KnowledgeFileDto>> {
    let url = `${this.apiKnowledge}/${this.knowledgeResource}/projects/${projectUUID}/files?page=${page}&size=${size}`;
    if (filter) url += `&filter=${encodeURIComponent(filter)}`;
    if (folderId !== undefined && folderId !== null) url += `&folderId=${folderId}`;
    return this.http.get<PageResult<KnowledgeFileDto>>(url);
  }

  sendQuestion(request: KnowledgeChatRequest): Observable<KnowledgeChatResponse> {
    return this.http.post<KnowledgeChatResponse>(`${this.apiKnowledge}/${this.knowledgeResource}/query`, request);
  }

  sendQuestionStream(request: KnowledgeChatRequest, token: string): Observable<StreamEvent> {
    const subject = new Subject<StreamEvent>();
    const url = `${this.apiKnowledge}/${this.knowledgeResource}/query/stream`;

    const yieldToRenderer = (): Promise<void> =>
      new Promise(resolve => requestAnimationFrame(() => resolve()));

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      },
      body: JSON.stringify(request)
    }).then(async (response) => {
      if (!response.ok) {
        const errorBody = await response.text();
        this.ngZone.run(() => {
          subject.error({ status: response.status, message: errorBody });
          subject.complete();
        });
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        const events = this.parseSseEvents(buffer);
        buffer = events.remaining;

        for (const sseEvent of events.parsed) {
          if (sseEvent.event === 'metadata') {
            this.ngZone.run(() => {
              try {
                subject.next({ type: 'metadata', data: JSON.parse(sseEvent.data) });
              } catch {
                subject.next({ type: 'metadata', data: sseEvent.data });
              }
            });
          } else if (sseEvent.event === 'answer') {
            let text = sseEvent.data;
            try { text = JSON.parse(text); } catch { /* use raw text as fallback */ }
            this.ngZone.run(() => {
              subject.next({ type: 'answer', data: text });
            });
            await yieldToRenderer();
          } else if (sseEvent.event === 'tool_call' || sseEvent.event === 'tool_result') {
            let data: any = sseEvent.data;
            try { data = JSON.parse(sseEvent.data); } catch { /* use raw text as fallback */ }
            this.ngZone.run(() => {
              subject.next({ type: sseEvent.event as 'tool_call' | 'tool_result', data });
            });
          } else if (sseEvent.event === 'done') {
            this.ngZone.run(() => {
              subject.next({ type: 'done', data: null });
              subject.complete();
            });
            return;
          }
        }
      }

      this.ngZone.run(() => {
        if (!subject.closed) {
          subject.next({ type: 'done', data: null });
          subject.complete();
        }
      });
    }).catch((error) => {
      this.ngZone.run(() => {
        subject.error(error);
      });
    });

    return subject.asObservable();
  }

  private parseSseEvents(buffer: string): { parsed: { event: string; data: string }[]; remaining: string } {
    const parsed: { event: string; data: string }[] = [];

    // SSE events are separated by a blank line. The spec admits LF, CR, or
    // CRLF as line terminators — sse-starlette (servidor) usa CRLF, así que
    // la separación entre eventos llega como \r\n\r\n. Usamos un regex que
    // tolera ambos para no quedarnos esperando un \n\n que nunca llega.
    const blocks = buffer.split(/\r?\n\r?\n/);
    // Last block may be incomplete
    const remaining = blocks.pop() || '';

    for (const block of blocks) {
      if (!block.trim()) continue;

      let eventType = '';
      const dataLines: string[] = [];

      for (const line of block.split(/\r?\n/)) {
        if (line.startsWith('event:')) {
          eventType = line.substring(6).trim();
        } else if (line.startsWith('data:')) {
          // El valor sigue al prefijo `data:` (con espacio opcional, según
          // spec). Trim sólo de espacios iniciales para no comerse blancos
          // significativos dentro del payload JSON.
          const raw = line.substring(5);
          dataLines.push(raw.startsWith(' ') ? raw.substring(1) : raw);
        }
      }

      if (eventType || dataLines.length > 0) {
        parsed.push({
          event: eventType || 'message',
          data: dataLines.join('\n')
        });
      }
    }

    return { parsed, remaining };
  }

  deleteFile(id: string): Observable<ResponseProcessInfoDto> {
    return this.http.delete<ResponseProcessInfoDto>(`${this.apiKnowledge}/${this.knowledgeResource}/files/${id}`);
  }

  deleteMultipleFiles(fileIds: string[]): Observable<any> {
    return this.http.delete<any>(`${this.apiKnowledge}/${this.knowledgeResource}/files/batch`, {
      body: fileIds
    });
  }

  createProject(id: string): Observable<KnowledgeProjectDTO> {
    return this.http.delete<KnowledgeProjectDTO>(`${this.apiKnowledge}/${this.knowledgeResource}/projects`)
  }

  downloadFile(fileId: string, isAdmin: boolean): Observable<Blob> {
    const headers = new HttpHeaders({
      'X-User-Role': isAdmin ? 'admin' : 'user',
      'Content-Type': 'application/json'
    });
    return this.http.get(`${this.apiKnowledge}/${this.knowledgeResource}/files/${fileId}/download`, {
      headers,
      responseType: 'blob'
    });
  }

  synchronizeSharePointFiles(projectId: string): Observable<SharePointSyncResponse> {
    return this.withMsalToken(token =>
      this.http.post<SharePointSyncResponse>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/synchronize`,
        null,
        {
          params: { projectId },
          headers: this.sharePointHeaders(token)
        }
      )
    );
  }

  getSharePointItems(projectId: string, path: string): Observable<any> {
    return this.http.get<any>(`${this.apiKnowledge}/${this.knowledgeResource}/files/sharepoint-items`, {
      params: { projectId, path }
    });
  }

  addFilesFromSharePoint(projectId: string, fileIds: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiKnowledge}/${this.knowledgeResource}/files/add-from-sharepoint`, fileIds, {
      params: { projectId }
    });
  }

  // Primero obtengo el token MSAL y luego hago la petición con ese token en el header
  private withMsalToken<T>(request: (token: string) => Observable<T>): Observable<T> {
    return from(this.authService.getMsalAccessToken()).pipe(
      switchMap(token => request(token))
    );
  }

  // Cabecera que exige el back para todos los endpoints de SharePoint
  private sharePointHeaders(msalToken: string): HttpHeaders {
    return new HttpHeaders({ 'msal-token': msalToken });
  }

  // Lista de sites a los que tiene acceso el usuario (con token MSAL)
  getSharePointSites(): Observable<SharePointSiteInfo[]> {
    return this.withMsalToken(token =>
      this.http.get<SharePointSiteInfo[]>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/sites`,
        { headers: this.sharePointHeaders(token) }
      )
    );
  }

  // Búsqueda de sites por nombre (mismo token MSAL)
  searchSharePointSites(query: string): Observable<SharePointSiteInfo[]> {
    return this.withMsalToken(token =>
      this.http.get<SharePointSiteInfo[]>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/sites/search`,
        { params: { query }, headers: this.sharePointHeaders(token) }
      )
    );
  }

  // Drives (bibliotecas/documentos) de un site concreto
  getSharePointDrives(siteId: string): Observable<SharePointDriveInfo[]> {
    return this.withMsalToken(token =>
      this.http.get<SharePointDriveInfo[]>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/sites/${encodeURIComponent(siteId)}/drives`,
        { headers: this.sharePointHeaders(token) }
      )
    );
  }

  // Ficheros y carpetas en la raíz del drive
  getSharePointDriveFiles(driveId: string): Observable<SharePointFileInfo[]> {
    return this.withMsalToken(token =>
      this.http.get<SharePointFileInfo[]>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/drives/${encodeURIComponent(driveId)}/files`,
        { headers: this.sharePointHeaders(token) }
      )
    );
  }

  // Contenido de una carpeta dentro del drive (para navegar por subcarpetas)
  getSharePointFolderFiles(driveId: string, folderId: string): Observable<SharePointFileInfo[]> {
    return this.withMsalToken(token =>
      this.http.get<SharePointFileInfo[]>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/drives/${encodeURIComponent(driveId)}/folders/${encodeURIComponent(folderId)}/files`,
        { headers: this.sharePointHeaders(token) }
      )
    );
  }

  // Detalle de un fichero concreto (por si lo necesitamos después)
  getSharePointFileDetail(driveId: string, fileId: string): Observable<SharePointFileInfo> {
    return this.withMsalToken(token =>
      this.http.get<SharePointFileInfo>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/drives/${encodeURIComponent(driveId)}/files/${encodeURIComponent(fileId)}`,
        { headers: this.sharePointHeaders(token) }
      )
    );
  }

  // Importar un fichero concreto desde SharePoint (Authorization la añade el interceptor, msal-token nosotros)
  importSharePointFile(request: SharePointImportRequest): Observable<SharePointImportResponse> {
    return this.withMsalToken(token =>
      this.http.post<SharePointImportResponse>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/import`,
        request,
        {
          params: { projectId: request.projectId },
          headers: this.sharePointHeaders(token)
        }
      )
    );
  }
  

  // Importar varios ficheros desde SharePoint (batch)
  importSharePointFilesBatch(request: SharePointBatchImportRequest): Observable<SharePointBatchImportResponse> {
    return this.withMsalToken(token =>
      this.http.post<SharePointBatchImportResponse>(
        `${this.apiKnowledge}/${this.knowledgeResource}/sharepoint/import/batch`,
        request,
        {
          params: { projectId: request.projectId },
          headers: this.sharePointHeaders(token)
        }
      )
    );
    }
    
  getSuggestionsByProjectId(projectId: string): Observable<SuggestionDto[]> {
    const headers = new HttpHeaders({
      'queryParam': '5'
    });
    return this.http.get<SuggestionDto[]>(`${this.apiKnowledge}/${this.knowledgeResource}/suggestions/project/${projectId}/random`, { headers });
  }
  
  getFolders(projectId: string): Observable<KnowledgeFolderDto[]> {
    return this.http.get<KnowledgeFolderDto[]>(
      `${this.apiKnowledge}/${this.knowledgeResource}/folders?projectId=${projectId}`
    );
  }

  createFolder(projectId: string, name: string): Observable<KnowledgeFolderDto> {
    return this.http.post<KnowledgeFolderDto>(
      `${this.apiKnowledge}/${this.knowledgeResource}/folders?projectId=${projectId}`,
      { name }
    );
  }

  renameFolder(folderId: string, name: string): Observable<KnowledgeFolderDto> {
    return this.http.put<KnowledgeFolderDto>(
      `${this.apiKnowledge}/${this.knowledgeResource}/folders/${folderId}`,
      { name }
    );
  }

  deleteFolder(folderId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiKnowledge}/${this.knowledgeResource}/folders/${folderId}`
    );
  }

  moveFiles(fileIds: string[], targetFolderId: string | null): Observable<any> {
    return this.http.post<any>(
      `${this.apiKnowledge}/${this.knowledgeResource}/folders/move-files`,
      { fileIds, targetFolderId }
    );
  }

  getChatHistory(projectId: string, userId: string): Observable<ChatHistoryEntry[]> {
    return this.http.get<ChatHistoryEntry[]>(
      `${this.apiKnowledge}/${this.knowledgeResource}/query/chat-history`,
      { params: { projectId, userId } }
    );
  }

  /**
   * Carga la conversación completa de un chat por su id.
   * El listado `getChatHistory` ya no incluye la historia entera (solo
   * un preview), así que cuando el usuario clica una conversación hay
   * que llamar a este endpoint para repintar los mensajes.
   */
  getChatHistoryDetail(chatId: string): Observable<ChatHistoryDetail> {
    return this.http.get<ChatHistoryDetail>(
      `${this.apiKnowledge}/${this.knowledgeResource}/query/chat-history/${chatId}`
    );
  }

  uploadMultipleFilesToFolder(projectId: string, files: File[], folderId: string | null): Observable<any> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('projectId', projectId);
    let url = `${this.apiKnowledge}/${this.knowledgeResource}/files/upload-files?projectId=${projectId}`;
    if (folderId) url += `&folderId=${folderId}`;
    return this.http.post<any>(url, formData);
  }
}

